/*
 * @author BSG <dev@bsgroup.eu>
 * @copyright Better Software Group S.A.
 * @version: 1.0
 */

import {
  ImageHelper,
  MediaType,
  RouteHelper,
  useCanWatchAuthContent,
} from "@nf/common";
import dynamic from "next/dynamic";
import cx from "classnames";
import { AppImage } from "components/AppImage/AppImage";

import { IListComponentItemProps } from "../../models";

const MediaAdditionalInfo = dynamic(
  () => import("components/Media/MediaAdditionalInfo")
);

const PlayButton = dynamic(() => import("components/PlayButton"));

const MyListButton = dynamic(() => import("components/MyListButton"));

const LinkWithLocale = dynamic(() => import("components/LinkWithLocale"));

import styles from "./ListComponentItemHighlight.module.scss";
import { useSyncCompleted } from "../../../../../../../hooks";

type IListComponentItemHighlightProps = IListComponentItemProps;

export const ListComponentItemHighlight = ({
  media,
  isPlaceholder,
  index,
}: IListComponentItemHighlightProps) => {
  const { canWatch } = useCanWatchAuthContent(media);
  const isAuthProcessCompleted = useSyncCompleted();

  if (!media || isPlaceholder) {
    return <div className={cx(styles.item, styles.background)} />;
  }

  const canAddToWatchlist =
    canWatch && media.MediaTypeCode !== MediaType.Collection;

  return (
    <LinkWithLocale href={RouteHelper.goToDetails(media)}>
      <a className={cx(styles.item, styles.background)}>
        <AppImage
          src={ImageHelper.getHeroFrontpageUrl(media?.Images) || ""}
          alt={media.Title || ""}
          sizes="100vw"
          fill={true}
          isLCP={index === 0}
          className={styles.image}
        />

        <div className={styles.container}>
          <div className={styles.content}>
            <h1 className={styles.title}>{media?.Description}</h1>
            <div className={styles.infoContainer}>
              <p className={styles.description}>{media.Title}</p>
              <MediaAdditionalInfo
                className={styles.additionalDescription}
                media={media}
                separator=" | "
              />
            </div>
            <div className={styles.buttonContainer}>
              {isAuthProcessCompleted && (
                <>
                  <PlayButton isStopPropagation={true} media={media} />

                  {canAddToWatchlist && (
                    <MyListButton showLabel={false} media={media} />
                  )}
                </>
              )}
            </div>
          </div>
        </div>
      </a>
    </LinkWithLocale>
  );
};
